import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import cx from "classnames";

import InfiniteScroll from "src/components/infiniteScroll";

import styles from "./learnings.module.scss";
import { useNavigate } from "react-router-dom";
import { fetchMyCourses } from "src/redux/actions/coaching/learners";
import { LearningRecordSummary } from "src/types/coaching/learners";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseCard, PlaceholderCourseCard } from "./card";

type CourseStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

const COURSE_CATEGORIES = [
  { label: "assigned", status: "NOT_STARTED" as CourseStatus, key: 'notStarted' },
  { label: "in progress", status: "IN_PROGRESS" as CourseStatus, key: 'inProgress' },
  { label: "completed", status: "COMPLETED" as CourseStatus, key: 'completed' },
];

interface CourseDataProps {
  loading: boolean;
  courses: LearningRecordSummary[];
  hasMoreItems: boolean;
  nextPage: number | null;
  total: number;
  // eslint-disable-next-line no-unused-vars
  fetchCoursesForPage: (page: number) => void;
}

const useCourses = (status: string): CourseDataProps => {
  const dispatch = useDispatch();

  const { courses, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const key = COURSE_CATEGORIES.find((item) => item.status === status)?.key;

    const coursesState = _.get(state, `learners.courses.${key}`);

    const courses = _.get(coursesState, 'items') || [] as LearningRecordSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);


  const fetchCoursesForPage = (page: number): void => {
    dispatch(fetchMyCourses({ status, page, limit: 5 }));
  };

  useEffect(() => {
    fetchCoursesForPage(0);
  }, []);

  return {
    loading,
    courses,
    hasMoreItems,
    nextPage,
    total,
    fetchCoursesForPage
  };
};

interface CategorySectionProps {
  label: string;
  status: CourseStatus;
}

const CategorySection: React.FC<CategorySectionProps> = ({ label, status }) => {
  const navigate = useNavigate();
  const { loading, courses, hasMoreItems, nextPage, total, fetchCoursesForPage } = useCourses(status);

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(nextPage);
  };

  const handleClick = (record: LearningRecordSummary) => {
    if(record.course?.entityType === "simulation") {
      navigate(`/coaching/simulations/${record.id}`);
    } else if(record.course?.entityType === "course") {
      navigate(`/coaching/courses/${record.id}`);
    } else if(record.course?.entityType === "podcast") {
      navigate(`/coaching/podcasts/${record.id}`);
    }
  }

  return (
    <div className={styles.contentDetails}>
      <div className={styles.left}>
        <div className={styles.title}>
          <p className={styles.label}></p>
          Courses <p className={styles.status}>{label}</p>
        </div>
        {!loading &&
          <p className={styles.courseCount}>
            {total.toString().padStart(2, "0")}
          </p>
        }
      </div>
      <div className={styles.right}>
        <InfiniteScroll
          key={status}
          className={styles.courseList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreCourses}
          loader={() => {
            return _.times(_.size(courses) == 0 ? 4 : 1).map((i) => (
              <PlaceholderCourseCard key={i} />
            ));
          }}
        >
          {courses.map((course, index) => (
            <CourseCard
              key={course.id || index}
              course={course}
              status={status}
              onClick={handleClick}
            />
          ))}
        </InfiniteScroll>
      </div>
    </div>
  );
};

const Learnings: React.FC = () => {
  if(!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  return (
    <div className={styles.container}>
      <LearningSummary />
      <div className={styles.content}>
        {COURSE_CATEGORIES.map(({ label, status }) => (
          <CategorySection
            key={status}
            label={label}
            status={status}
          />
        ))}
      </div>
    </div>
  );
};

const LearningSummaryPlaceholder = () => {
  return (
    <div className={cx(styles.header, styles.placeholder)}>
      <div className={styles.block}></div>
      <div className={styles.block}></div>
    </div>
  )
}

const LearningSummary = () => {
  const loading = useSelector((state: any) => {
    return _.get(state, "learners.courses.notStarted.loading") &&
      _.get(state, "learners.courses.inProgress.loading") &&
      _.get(state, "learners.courses.completed.loading");
  });

  const { assigned, inProgress, completed } = useSelector((state: any) => {
    return {
      assigned: _.get(state, "learners.courses.notStarted.pagination.totalRecordsCount") || 0,
      inProgress: _.get(state, "learners.courses.inProgress.pagination.totalRecordsCount") || 0,
      completed: _.get(state, "learners.courses.completed.pagination.totalRecordsCount") || 0,
    };
  });

  if (loading) {
    return <LearningSummaryPlaceholder />;
  }

  return (
    <div className={cx(styles.header)}>
      you have <span className={styles.count}>{assigned.toString().padStart(2, "0")}</span> new course{assigned > 1 ? 's' : ''}
      &nbsp;assigned to you, <span className={styles.count}>{inProgress.toString().padStart(2, "0")}</span> in progress and <span className={styles.count}>{completed.toString().padStart(2, "0")}</span> completed course{completed > 1 ? 's' : ''} in total.
    </div>
  )
}

export { Learnings };